import React, { useRef, useState, useEffect } from 'react';
import { Button } from './ui/button';
import { BookOpen, Heart, Sparkles, Download, Star, Shield, GraduationCap, Smile, Users, Lock, QrCode, Package, ArrowRight, Check, Mail } from 'lucide-react';
import CorporateHeader from './CorporateHeader';

const BOOK_COVER = 'https://customer-assets-agu9un31.emergentagent.net/job_714c4da9-c687-4be7-8ba1-ec382daddaca/artifacts/ab4749f59fe17a6f_A%26M%20Page%200.png';

// ⚠️ PREVIEW MODE — Flip to true ONLY after the real Stripe payment link is pasted below
// AND the PDF has been uploaded to /app/backend/digital_products/ava_and_maverick_go_to_school.pdf
const BOOK_LIVE = true;
const BOOK_STRIPE_LINK = 'https://buy.stripe.com/6oUfZjewG4ckavqaIUebu06';

// Public sales URL (used by the QR code — points customers back to this page)
const SALES_URL = 'https://eblackbooklistings.com/ava-and-maverick';
const QR_IMG = `https://api.qrserver.com/v1/create-qr-code/?size=240x240&margin=10&bgcolor=FFFFFF&color=1E3A8A&data=${encodeURIComponent(SALES_URL)}`;

const themes = [
  { icon: Smile, color: 'from-pink-500 to-rose-500', text: 'Friendship' },
  { icon: GraduationCap, color: 'from-blue-500 to-indigo-500', text: 'Learning' },
  { icon: Heart, color: 'from-red-500 to-pink-500', text: 'Kindness' },
  { icon: Users, color: 'from-amber-400 to-yellow-500', text: 'Sharing' },
  { icon: Sparkles, color: 'from-purple-500 to-fuchsia-500', text: 'Big Dreams' },
];

const AvaMaverickPage = () => {
  const buyRef = useRef(null);
  const [previewClicked, setPreviewClicked] = useState(false);

  // Owner can bypass the Coming Soon splash with ?preview=1 (or just ?preview)
  // Captured once in a useState initializer so it's locked in on first render,
  // and persisted to localStorage so the owner can navigate the full page
  // without re-adding the query param every time.
  const [isPreviewBypass] = useState(() => {
    if (typeof window === 'undefined') return false;
    try {
      const params = new URLSearchParams(window.location.search);
      // Explicit opt-out clears any stored flag
      if (params.has('nopreview') || params.get('preview') === '0') {
        try { window.localStorage.removeItem('am_preview'); } catch (_) {}
        return false;
      }
      if (params.has('preview')) {
        const v = params.get('preview');
        if (v === null || v === '' || v === '1' || v === 'true') {
          try { window.localStorage.setItem('am_preview', '1'); } catch (_) {}
          return true;
        }
      }
      return window.localStorage.getItem('am_preview') === '1';
    } catch (_) {
      return false;
    }
  });

  const showSplash = !BOOK_LIVE && !isPreviewBypass;

  // Inject noindex meta tag whenever the book isn't live yet
  useEffect(() => {
    if (BOOK_LIVE) return;
    const meta = document.createElement('meta');
    meta.name = 'robots';
    meta.content = 'noindex, nofollow';
    document.head.appendChild(meta);
    const prevTitle = document.title;
    document.title = 'Coming Soon — Ava & Maverick Go To School';
    return () => {
      document.head.removeChild(meta);
      document.title = prevTitle;
    };
  }, []);

  const scrollToBuy = () => buyRef.current?.scrollIntoView({ behavior: 'smooth' });

  const handlePreviewClick = (e) => {
    if (!BOOK_LIVE) {
      e.preventDefault();
      setPreviewClicked(true);
    }
  };

  // ===== COMING SOON SPLASH (when the book is not live yet) =====
  if (showSplash) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-pink-100 via-amber-100 to-sky-100 relative overflow-hidden" data-testid="am-coming-soon-splash">
        {/* Playful floating decorations */}
        <div className="absolute top-10 left-10 w-24 h-24 bg-yellow-300 rounded-full opacity-50 blur-2xl" />
        <div className="absolute top-1/3 right-10 w-32 h-32 bg-pink-300 rounded-full opacity-50 blur-3xl" />
        <div className="absolute bottom-10 left-1/3 w-28 h-28 bg-sky-300 rounded-full opacity-50 blur-2xl" />

        <CorporateHeader />

        <div className="relative pt-28 pb-16 container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 bg-white/70 backdrop-blur border-2 border-amber-300 rounded-full px-5 py-2 mb-6 shadow-sm">
              <Sparkles className="w-4 h-4 text-amber-600" />
              <span className="text-amber-800 text-xs sm:text-sm font-black tracking-widest uppercase">Coming Soon</span>
            </div>

            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black leading-tight mb-5" style={{ fontFamily: '"Fredoka", "Comic Sans MS", system-ui, sans-serif' }}>
              <span className="block text-slate-800">The Adventures of</span>
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-pink-500 via-amber-500 to-blue-500">AVA &amp; MAVERICK</span>
              <span className="block text-slate-800 text-3xl sm:text-4xl lg:text-5xl mt-1">Go To School</span>
            </h1>

            <p className="text-lg sm:text-xl text-slate-700 italic mb-10" style={{ fontFamily: 'Georgia, serif' }}>
              A story about friendship, learning, and all the amazing things we can do.
            </p>

            <div className="relative max-w-xs sm:max-w-sm mx-auto mb-10">
              <div className="absolute -inset-4 bg-gradient-to-r from-yellow-200 via-pink-200 to-sky-200 rounded-3xl blur-2xl opacity-70" />
              <img
                src={BOOK_COVER}
                alt="The Adventures of Ava and Maverick Go to School — book cover"
                className="relative w-full rounded-2xl shadow-2xl border-4 border-white"
                data-testid="am-splash-cover"
              />
            </div>

            <div className="bg-white/80 backdrop-blur border-2 border-pink-200 rounded-3xl p-6 sm:p-8 max-w-md mx-auto shadow-xl">
              <p className="text-slate-700 text-base sm:text-lg mb-2">
                Our story is almost ready to meet you.
              </p>
              <p className="text-slate-500 text-sm mb-5">
                A joyful 36-page picture book by <span className="font-bold text-slate-700">Audrey Egypt Young</span> — launching very soon.
              </p>
              <a href="mailto:info@eBlackBookListings.com?subject=Notify%20me%20when%20Ava%20%26%20Maverick%20launches" className="inline-block">
                <Button className="bg-gradient-to-r from-pink-500 via-rose-500 to-amber-500 hover:from-pink-600 hover:via-rose-600 hover:to-amber-600 text-white font-bold px-7 py-5 text-base rounded-full shadow-lg min-h-[52px]" data-testid="am-notify-button">
                  <Mail className="w-5 h-5 mr-2" />
                  Notify me at launch
                </Button>
              </a>
              <p className="text-xs text-slate-400 mt-4">We'll email you the moment the book goes live.</p>
            </div>

            <p className="mt-10 text-amber-700 text-xs font-semibold tracking-widest uppercase">eBlackBookListings</p>
          </div>
        </div>
      </div>
    );
  }

  // ===== FULL SALES PAGE (shown when BOOK_LIVE=true OR ?preview=1 param present) =====

  return (
    <div className="min-h-screen bg-gradient-to-b from-sky-50 via-amber-50 to-pink-50">
      <CorporateHeader />

      {/* Preview-mode admin banner */}
      {!BOOK_LIVE && (
        <div className="pt-20 bg-amber-100 border-b-2 border-amber-400" data-testid="preview-mode-banner">
          <div className="container mx-auto px-4 py-2 flex items-center justify-center gap-2 text-amber-900 text-xs sm:text-sm font-semibold text-center">
            <Lock className="w-4 h-4 flex-shrink-0" />
            <span>PREVIEW MODE — Purchase button not yet active. Payment system is being verified.</span>
          </div>
        </div>
      )}

      {/* ===== HERO ===== */}
      <section className={`${BOOK_LIVE ? 'pt-28' : 'pt-10'} pb-16 relative overflow-hidden`}>
        {/* Playful floating decorations */}
        <div className="absolute top-10 left-6 w-16 h-16 bg-yellow-300 rounded-full opacity-40 blur-xl" />
        <div className="absolute top-32 right-10 w-24 h-24 bg-pink-300 rounded-full opacity-40 blur-2xl" />
        <div className="absolute bottom-10 left-1/4 w-20 h-20 bg-sky-300 rounded-full opacity-40 blur-xl" />

        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="grid lg:grid-cols-2 gap-10 items-center">
            {/* LEFT: Copy */}
            <div className="text-center lg:text-left order-2 lg:order-1">
              <div className="inline-flex items-center gap-2 bg-pink-100 border-2 border-pink-300 rounded-full px-4 py-1.5 mb-5">
                <Sparkles className="w-4 h-4 text-pink-600" />
                <span className="text-pink-700 text-xs sm:text-sm font-bold tracking-wide uppercase">Ages 7 &amp; Under</span>
              </div>

              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black leading-tight mb-4" style={{ fontFamily: '"Fredoka", "Comic Sans MS", system-ui, sans-serif' }} data-testid="am-hero-title">
                <span className="block text-slate-800">The Adventures of</span>
                <span className="block text-transparent bg-clip-text bg-gradient-to-r from-pink-500 via-amber-500 to-blue-500">AVA &amp; MAVERICK</span>
                <span className="block text-slate-800 text-3xl sm:text-4xl lg:text-5xl mt-1">Go To School</span>
              </h1>

              <p className="text-lg sm:text-xl text-slate-700 italic mb-2" style={{ fontFamily: 'Georgia, serif' }}>
                A story about friendship, learning, and all the amazing things we can do.
              </p>
              <p className="text-slate-600 mb-6">
                Written by <span className="font-bold text-slate-800">Audrey Egypt Young</span>
              </p>

              {/* Theme pill chips */}
              <div className="flex flex-wrap justify-center lg:justify-start gap-2 mb-7">
                {themes.map((t) => {
                  const I = t.icon;
                  return (
                    <span key={t.text} className={`inline-flex items-center gap-1.5 bg-gradient-to-r ${t.color} text-white text-xs sm:text-sm font-bold px-3 py-1.5 rounded-full shadow-sm`}>
                      <I className="w-3.5 h-3.5" />
                      {t.text}
                    </span>
                  );
                })}
              </div>

              {/* Price + CTA */}
              <div className="flex flex-col sm:flex-row items-center gap-4 justify-center lg:justify-start">
                <div className="text-center sm:text-left">
                  <p className="text-slate-500 text-xs uppercase tracking-widest font-bold">Digital PDF</p>
                  <p className="text-5xl font-black text-slate-800" data-testid="am-price">$9.99</p>
                  <p className="text-slate-500 text-xs">36 colorful pages</p>
                </div>
                <Button
                  onClick={scrollToBuy}
                  className="bg-gradient-to-r from-pink-500 via-rose-500 to-amber-500 hover:from-pink-600 hover:via-rose-600 hover:to-amber-600 text-white font-bold px-8 py-6 text-base sm:text-lg rounded-full shadow-xl shadow-pink-200 min-h-[56px]"
                  data-testid="am-hero-cta"
                >
                  <BookOpen className="w-5 h-5 mr-2" />
                  Get the Book
                </Button>
              </div>
            </div>

            {/* RIGHT: Cover */}
            <div className="order-1 lg:order-2 flex justify-center">
              <div className="relative max-w-sm sm:max-w-md w-full">
                <div className="absolute -inset-4 bg-gradient-to-r from-yellow-200 via-pink-200 to-sky-200 rounded-3xl blur-2xl opacity-70" />
                <div className="relative transform rotate-[-2deg] hover:rotate-0 transition-transform duration-500">
                  <img
                    src={BOOK_COVER}
                    alt="The Adventures of Ava and Maverick Go to School — book cover"
                    className="relative w-full rounded-2xl shadow-2xl border-4 border-white"
                    data-testid="am-book-cover"
                  />
                  <div className="absolute -top-4 -right-4 bg-yellow-400 text-slate-900 font-black text-xs px-3 py-2 rounded-full border-4 border-white shadow-lg rotate-6">
                    NEW!
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== WHAT'S INSIDE ===== */}
      <section className="py-16 bg-white border-y-4 border-dashed border-amber-200">
        <div className="container mx-auto px-4 max-w-5xl">
          <h2 className="text-3xl sm:text-4xl font-black text-center text-slate-800 mb-3" style={{ fontFamily: '"Fredoka", "Comic Sans MS", system-ui, sans-serif' }}>What's Inside</h2>
          <p className="text-center text-slate-600 mb-12 text-lg">A joyful 36-page journey built for little readers.</p>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {[
              { icon: BookOpen, title: '36 Full Pages', desc: 'Bright illustrations on every page', color: 'bg-sky-100 text-sky-700 border-sky-200' },
              { icon: Heart, title: 'Kindness First', desc: 'Lessons about caring for friends', color: 'bg-pink-100 text-pink-700 border-pink-200' },
              { icon: GraduationCap, title: 'Learning Fun', desc: 'A big new day at school', color: 'bg-amber-100 text-amber-700 border-amber-200' },
              { icon: Sparkles, title: 'Big Dreams', desc: 'Encourages kids to grow bold', color: 'bg-purple-100 text-purple-700 border-purple-200' },
            ].map((f) => {
              const I = f.icon;
              return (
                <div key={f.title} className={`${f.color} border-2 rounded-2xl p-6 text-center transition-transform hover:-translate-y-1`}>
                  <I className="w-10 h-10 mx-auto mb-3" />
                  <h3 className="font-black text-lg mb-1">{f.title}</h3>
                  <p className="text-sm">{f.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ===== BUY SECTION ===== */}
      <section ref={buyRef} className="py-20 relative" id="buy">
        <div className="absolute inset-0 bg-gradient-to-br from-pink-100 via-amber-100 to-sky-100" />
        <div className="relative container mx-auto px-4 max-w-3xl">
          <div className="bg-white rounded-3xl shadow-2xl border-4 border-amber-200 overflow-hidden">
            <div className="bg-gradient-to-r from-pink-500 via-rose-500 to-amber-500 text-white text-center py-5">
              <h2 className="text-3xl sm:text-4xl font-black" style={{ fontFamily: '"Fredoka", "Comic Sans MS", system-ui, sans-serif' }}>
                Instant Digital Download
              </h2>
              <p className="text-pink-50 text-sm mt-1">Read on any phone, tablet, or computer</p>
            </div>

            <div className="p-6 sm:p-10 text-center">
              <div className="flex items-baseline justify-center gap-2 mb-2">
                <span className="text-slate-400 line-through text-lg">$14.99</span>
                <span className="text-6xl sm:text-7xl font-black text-slate-800">$9.99</span>
              </div>
              <p className="text-slate-500 text-sm mb-6">One-time payment &bull; Lifetime access &bull; 36-page PDF</p>

              {/* Features list */}
              <ul className="space-y-2 text-left max-w-md mx-auto mb-8">
                {['36 full-color illustrated pages', 'Instant PDF download after payment', 'Read anywhere, print at home', 'Perfect for ages 7 & under'].map((f) => (
                  <li key={f} className="flex items-start gap-2 text-slate-700">
                    <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>

              {/* Buy button — disabled in preview mode */}
              {BOOK_LIVE ? (
                <a href={BOOK_STRIPE_LINK} target="_blank" rel="noopener noreferrer">
                  <Button className="w-full bg-gradient-to-r from-pink-500 via-rose-500 to-amber-500 hover:from-pink-600 hover:via-rose-600 hover:to-amber-600 text-white font-black py-6 text-lg rounded-full shadow-lg min-h-[60px]" data-testid="am-buy-button">
                    <Download className="w-5 h-5 mr-2" />
                    Buy Now — $9.99
                  </Button>
                </a>
              ) : (
                <Button
                  onClick={handlePreviewClick}
                  disabled
                  className="w-full bg-slate-300 text-slate-600 cursor-not-allowed font-black py-6 text-lg rounded-full min-h-[60px] opacity-80"
                  data-testid="am-buy-button-disabled"
                >
                  <Lock className="w-5 h-5 mr-2" />
                  Launching Soon
                </Button>
              )}

              {!BOOK_LIVE && previewClicked && (
                <p className="mt-4 text-amber-800 bg-amber-100 border border-amber-300 rounded-xl px-4 py-3 text-sm" data-testid="am-preview-message">
                  🔒 This button goes live once the payment flow is verified.
                </p>
              )}

              <p className="text-xs text-slate-500 mt-4 flex items-center justify-center gap-1">
                <Shield className="w-3 h-3" />
                Secure checkout &bull; Powered by Stripe
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ===== QR CODE + PRINT EDITION ===== */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 max-w-5xl">
          <div className="grid md:grid-cols-2 gap-6">
            {/* QR Card */}
            <div className="bg-gradient-to-br from-sky-50 to-blue-50 border-2 border-sky-200 rounded-3xl p-8 text-center" data-testid="am-qr-card">
              <QrCode className="w-10 h-10 text-sky-600 mx-auto mb-3" />
              <h3 className="text-2xl font-black text-slate-800 mb-2">Share with Friends</h3>
              <p className="text-slate-600 text-sm mb-5">Scan this QR code to visit the book's page.</p>
              <div className="bg-white inline-block p-4 rounded-2xl shadow-md border-2 border-sky-100 mb-4">
                <img src={QR_IMG} alt="QR code to the Ava and Maverick sales page" className="w-48 h-48" data-testid="am-qr-image" />
              </div>
              <p className="text-xs text-slate-500 break-all">{SALES_URL}</p>
            </div>

            {/* Printed Edition (Coming Soon) */}
            <div className="bg-gradient-to-br from-amber-50 to-rose-50 border-2 border-dashed border-amber-300 rounded-3xl p-8 text-center flex flex-col justify-center" data-testid="am-print-card">
              <Package className="w-10 h-10 text-amber-600 mx-auto mb-3" />
              <h3 className="text-2xl font-black text-slate-800 mb-2">Printed Edition</h3>
              <div className="inline-block bg-amber-200 text-amber-900 text-xs font-black px-3 py-1 rounded-full mx-auto mb-4 tracking-wider">
                COMING SOON
              </div>
              <p className="text-slate-600 text-sm mb-5">
                A beautiful hardcover edition is on the way — perfect for bedtime shelves, classrooms, and gifts.
              </p>
              <Button disabled className="bg-slate-200 text-slate-500 cursor-not-allowed font-bold py-5 rounded-full mx-auto px-8" data-testid="am-print-notify">
                Notify Me (Launching Soon)
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* ===== ABOUT THE AUTHOR ===== */}
      <section className="py-16 bg-gradient-to-br from-slate-900 to-slate-800 text-white">
        <div className="container mx-auto px-4 max-w-3xl text-center">
          <Star className="w-10 h-10 text-amber-400 mx-auto mb-4" />
          <h2 className="text-3xl font-black mb-4" style={{ fontFamily: '"Fredoka", "Comic Sans MS", system-ui, sans-serif' }}>About the Author</h2>
          <h3 className="text-xl text-amber-300 font-bold mb-3">Audrey Egypt Young</h3>
          <p className="text-slate-300 leading-relaxed">
            Audrey Egypt Young is a storyteller, publisher, and founder of eBlackBookListings — creating heartfelt books that help children see themselves as brave, kind, and ready for anything.
          </p>
        </div>
      </section>

      {/* Footer */}
      <div className="py-8 bg-slate-900 text-center">
        <p className="text-amber-400/80 text-sm font-semibold tracking-wider">eBlackBookListings</p>
        <p className="text-slate-500 text-xs mt-1">Books for growing minds &bull; Stories for brave hearts</p>
      </div>
    </div>
  );
};

export default AvaMaverickPage;
